// Stage 2b — "Connect Claude": mint a long-lived READ-ONLY MCP token
// for the signed-in user and hand them ready-to-paste setup.
//
// Flow: step-up (re-enter password) → POST /auth/mcp-token → show the
// token ONCE with copy buttons for (a) the raw token and (b) the full
// `claude mcp add` command. The token is read-only at the token level
// (the service rejects every mutating request carrying it) and scoped
// to THIS user by the service's RBAC.

import { useState } from "react";
import { Check, Copy } from "lucide-react";

import { createMcpToken, type McpTokenResponse } from "../lib/api";
import { Modal } from "./ui/Modal";
import { StepUpAuthModal } from "./StepUpAuthModal";

const MCP_URL = "https://mcp.slack-chat-backup.webmobtech.biz/mcp";

interface ConnectClaudeModalProps {
  onClose: () => void;
}

export function ConnectClaudeModal({ onClose }: ConnectClaudeModalProps) {
  const [result, setResult] = useState<McpTokenResponse | null>(null);

  if (result === null) {
    return (
      <StepUpAuthModal
        title="Connect Claude"
        description={
          <>
            This creates a <b>read-only</b> access token for Claude (valid ~90
            days, scoped to <b>your</b> workspaces only). Enter your password
            to generate it — it is shown once.
          </>
        }
        confirmLabel="Generate token"
        onConfirm={async (currentPassword) => {
          setResult(await createMcpToken(currentPassword));
        }}
        onClose={onClose}
      />
    );
  }

  const addCommand =
    `claude mcp add --transport http slack-archive ${MCP_URL} ` +
    `--header "Authorization: Bearer ${result.token}"`;

  return (
    <Modal open onClose={onClose} title="Claude is ready to connect" size="lg">
      <div className="space-y-5" data-testid="connect-claude-result">
        <p className="text-body-sm text-theme-text-secondary">
          Token created — read-only, expires{" "}
          <span className="text-theme-text-primary">{result.expires_at}</span>.
          It is shown <b>once</b>; treat it like a password.
        </p>

        <SecretBlock
          label="Your MCP token"
          value={result.token}
          testid="connect-claude-token"
        />

        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary mb-1.5">
            Claude Code — one command
          </div>
          <SecretBlock label={null} value={addCommand} testid="connect-claude-command" />
        </div>

        <div className="rounded-xl bg-white/[0.03] border border-white/[0.08] px-4 py-3 text-body-sm text-theme-text-secondary leading-relaxed">
          <b>claude.ai (web):</b> Settings → Connectors → Add custom connector →
          paste <span className="font-mono text-xs">{MCP_URL}</span> — then sign
          in with your archive account on the page that opens (OAuth). No token
          paste needed there.
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            data-testid="connect-claude-done"
            className="px-4 py-2 rounded-lg text-sm bg-theme-accent-bg text-theme-accent-text hover:opacity-90"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SecretBlock({
  label,
  value,
  testid,
}: {
  label: string | null;
  value: string;
  testid: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      {label && (
        <div className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary mb-1.5">
          {label}
        </div>
      )}
      <div className="flex items-start gap-2">
        <code
          data-testid={testid}
          className="flex-1 min-w-0 block px-3 py-2.5 rounded-xl bg-black/30 border border-white/[0.1] font-mono text-[11px] text-theme-text-primary break-all max-h-28 overflow-y-auto"
        >
          {value}
        </code>
        <button
          type="button"
          aria-label="Copy"
          data-testid={`${testid}-copy`}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            } catch {
              /* clipboard unavailable — user can select manually */
            }
          }}
          className="flex-shrink-0 w-9 h-9 rounded-xl bg-white/[0.05] border border-white/[0.1] flex items-center justify-center text-theme-text-secondary hover:text-theme-text-primary"
        >
          {copied ? (
            <Check className="w-4 h-4 text-theme-success-text" strokeWidth={2} aria-hidden />
          ) : (
            <Copy className="w-4 h-4" strokeWidth={1.75} aria-hidden />
          )}
        </button>
      </div>
    </div>
  );
}
